import { useState, useEffect } from 'react';
import UnsplashAttribution from './UnsplashAttribution';
import { triggerDownload } from '../utils/unsplashUtils';

/**
 * Unsplash image component with automatic attribution overlay
 * Use this instead of <img> for all Unsplash images
 * 
 * @param {Object} photo - Full Unsplash photo object from API
 * @param {string} alt - Alt text for accessibility
 * @param {string} className - CSS classes for the image
 * @param {Function} onClick - Optional click handler
 * @param {boolean} triggerDownloadOnClick - Whether to trigger download tracking on click (default: true)
 */
export default function UnsplashImage({ 
  photo, 
  alt, 
  className = '', 
  onClick,
  triggerDownloadOnClick = true 
}) {
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleClick = async (e) => {
    // Trigger download tracking if enabled
    if (triggerDownloadOnClick && photo?.links?.download_location) {
      await triggerDownload(photo.links.download_location);
    }
    
    // Call parent onClick if provided
    if (onClick) {
      onClick(e);
    }
  };

  if (!photo) {
    return (
      <div className={`bg-gray-200 ${className}`}>
        <div className="w-full h-full flex items-center justify-center text-gray-400">
          No image
        </div>
      </div>
    );
  }

  // Use 'regular' size by default, or 'small' for thumbnails
  const imageUrl = photo.urls?.regular || photo.urls?.small || photo.urls?.raw;

  return (
    <div className="relative w-full h-full" style={{ position: 'relative' }}>
      {/* Image */}
      <img
        src={imageUrl}
        alt={alt || photo.alt_description || 'Image from Unsplash'}
        className={`${className} w-full h-full object-cover`}
        onLoad={() => setImageLoaded(true)}
        onClick={handleClick}
        style={{ cursor: onClick ? 'pointer' : 'default' }}
      />

      {/* Attribution overlay - only show when image is loaded */}
      {imageLoaded && <UnsplashAttribution photo={photo} />}
    </div>
  );
}

